import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useRoute, useLocation, Link } from "wouter";
import { ArrowLeft, MapPin, Bookmark, ExternalLink, User, Share2, Zap, Navigation, Edit2, Clock } from "lucide-react";
import { getOpeningStatus } from "@/lib/openingHours";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShopCard } from "@/components/ShopCard";
import { PostCard } from "@/components/PostCard";
import { LoginModal } from "@/components/LoginModal";
import { UpgradeModal } from "@/components/UpgradeModal";
import { useAuth } from "@/lib/authContext";
import { getRouteById, dummyShops, dummyPosts } from "@/lib/dummyData";
import { InsiderStar } from "@/components/InsiderStar";
import { isInsider as checkInsiderStatus } from "@/lib/userTiers";
import { OSMRouteMap } from "@/components/OSMRouteMap";
import { useRouteInteractions } from "@/contexts/RouteInteractionsContext";
import { useRouteBuilder } from "@/contexts/RouteBuilderContext";
import { Heart } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ShopInteractionsProvider } from "@/contexts/ShopInteractionsContext";
import { logRouteNavAnalytics } from "@/lib/analytics";
import { ShareModal } from "@/components/ShareModal";
import { getStaticMapUrl } from "@/lib/googleMaps";
import { useI18n } from "@/lib/i18n";
import { TranslatableText } from "@/components/TranslatableText";
import { Star } from "lucide-react";
import { RouteReviews } from "@/components/RouteReviews";
import { checkUsageLimit, incrementUsage } from "@/lib/usageLimits";
import { SchemaMarkup } from "@/components/SchemaMarkup";
import { useSEO } from "@/hooks/useSEO";

export default function RouteDetailPage() {
  const [location, navigate] = useLocation();
  const { isLoggedIn, tier, user, isInsider } = useAuth();
  const { t, locale } = useI18n();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { clearRoute, addShop, setStartAddress: setContextStart, setEndAddress: setContextEnd, setIsCopiedRoute, setOriginalCopiedShopIds } = useRouteBuilder();
  const [route, setRoute] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { getInteraction, toggleFavorite, toggleWishlist, fetchInteractionsForRoutes } = useRouteInteractions();
  const interactions = route ? getInteraction(route.id) : { favorite: false, wishlist: false };
  const [isToggling, setIsToggling] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [showInteractiveMap, setShowInteractiveMap] = useState(false);

  const handleLoadInteractiveMap = () => {
    // Basic check: if already showing, do nothing
    if (showInteractiveMap) return;

    // Check limits
    const { canProceed, limit, period } = checkUsageLimit('mapView', tier);

    if (!canProceed) {
      toast({
        title: t("map.limitReachedTitle"),
        description: t(period === 'daily' ? "map.dailyLimitDesc" : "map.monthlyLimitDesc", { limit }),
        variant: "destructive"
      });
      return;
    }

    // Increment usage and show map
    incrementUsage('mapView');
    setShowInteractiveMap(true);
  };

  // Check for new slug-based route or legacy ID route
  const slugRoute = useRoute("/kringlooproutes/:slug");
  const legacyRoute = useRoute("/routes/:id");

  const slugParams = slugRoute[1];
  const legacyParams = legacyRoute[1];

  const routeSlug = slugParams?.slug;
  const legacyId = legacyParams?.id;

  useEffect(() => {
    async function fetchRoute() {
      if (!routeSlug && !legacyId) {
        setRoute(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const identifier = routeSlug || legacyId;
        if (!identifier) return;

        const response = await fetch(`/api/routes/${identifier}`);
        if (response.ok) {
          const routeData = await response.json();
          setRoute(routeData);
          if (isLoggedIn) {
            fetchInteractionsForRoutes([routeData.id]);
          }
        } else {
          console.error(`Route request failed with status: ${response.status}`);
          setRoute(null);
        }
      } catch (error) {
        console.error("Error fetching route:", error);
        setRoute(null);
      } finally {
        setLoading(false);
      }
    }
    fetchRoute();
  }, [routeSlug, legacyId, isLoggedIn]);

  // Handle ?share=true auto-open
  useEffect(() => {
    if (route && !loading) {
      if (typeof window !== 'undefined') {
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.get('share') === 'true') {
          // Add a small delay for smooth animation
          setTimeout(() => setShareModalOpen(true), 100);

          // Optionally remove the query param so refresh doesn't pop it again
          try {
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
          } catch (e) { }
        }
      }
    }
  }, [route, loading]);

  useSEO({
    title: route ? `${route.name} in ${route.city || route.region || t("routes.page.title")}` : t("routes.page.title"),
    description: route?.description || t("routes.page.description"),
  });

  const itinerarySchema = route ? {
    "@context": "https://schema.org",
    "@type": "TravelItinerary",
    "name": route.name,
    "description": route.description,
    "url": `https://vintmap.nl/routes/${route.slug || route.id}`,
    "itinerary": (route.stopsData || []).map((stop: any, index: number) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "LocalBusiness",
        "name": stop.name,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": stop.address,
          "addressLocality": stop.city,
          "addressRegion": stop.province,
          "addressCountry": stop.country || "NL"
        }
      }
    }))
  } : null;

  const generateMapsUrl = useCallback(() => {
    if (!route || !route.stopsData) return "#";
    const stops = route.stopsData;
    const points: string[] = [];

    // We intentionally exclude start_address from Maps URL so it opens from current location

    stops.forEach((shop: any) => {
      const name = typeof shop!.name === 'string' ? shop!.name : (shop!.name as any)?.value || "Winkel";
      const city = typeof shop!.city === 'string' ? shop!.city : (shop!.city as any)?.value || "Stad";
      points.push(encodeURIComponent(`${name}, ${city}`));
    });

    if (route.extra_stop && route.extra_stop.source !== 'current') {
      if (route.extra_stop.lat != null && route.extra_stop.lng != null) {
        points.push(`${route.extra_stop.lat},${route.extra_stop.lng}`);
      } else if (route.extra_stop.address) {
        points.push(encodeURIComponent(route.extra_stop.address));
      }
    }

    if (route.end_address && route.end_address.source !== 'current') {
      if (route.end_address.lat != null && route.end_address.lng != null) {
        points.push(`${route.end_address.lat},${route.end_address.lng}`);
      } else if (route.end_address.address) {
        points.push(encodeURIComponent(route.end_address.address));
      }
    }

    if (points.length === 0) return "#";

    // Empty originParam for free users so Google Maps defaults to current user location manually
    // For insiders, we force origin to current location for a 1-click start
    const originParam = isInsider ? "&origin=current+location" : "";

    if (points.length === 0) return "#";

    const destination = points.pop()!;
    const waypoints = points.join("|");
    const waypointsParam = waypoints ? `&waypoints=${waypoints}` : "";

    return `https://www.google.com/maps/dir/?api=1${originParam}&destination=${destination}${waypointsParam}`;
  }, [route]);

  const handleRouteCalculated = useCallback(async (metrics: any) => {
    // Only owner can save
    if (!route || !user || route.user_id !== user.id) return;

    // Avoid saving if already saved (basic check)
    if ((route.osm_route_data?.polyline || route.google_route_data?.polyline) === metrics.polyline) return;

    console.log("Saving calculated route metrics...", metrics);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const res = await fetch(`/api/routes/${route.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          osm_route_data: metrics
        })
      });

      if (res.ok) {
        console.log("Route metrics saved successfully");
        // Update local state to prevent loop
        setRoute((prev: any) => ({ ...prev, osm_route_data: metrics }));
      }
    } catch (e) {
      console.error("Failed to save route metrics", e);
    }
  }, [route, user]);

  const handleToggleAction = async (type: 'favorite' | 'wishlist') => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    if (isToggling || !route) return;
    setIsToggling(true);

    try {
      if (type === 'favorite') {
        const newState = await toggleFavorite(route.id);
        toast({
          title: newState ? t("routeDetail.addedFavorite") : t("routeDetail.removedFavorite"),
          duration: 2000,
        });
      } else {
        const newState = await toggleWishlist(route.id);
        toast({
          title: newState ? t("routeDetail.addedBucketlist") : t("routeDetail.removedBucketlist"),
          duration: 2000,
        });
      }
    } catch (error) {
      toast({
        title: t("routeDetail.errorTitle"),
        description: t("restyle.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setIsToggling(false);
    }
  };

  const handlePersonalize = () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    if (!isInsider) {
      setShowUpgradeModal(true);
      return;
    }

    // Prepare route builder with these stops
    clearRoute();
    setIsCopiedRoute(true);
    if (route?.stopsData) {
      route.stopsData.forEach((shop: any) => addShop(shop));
      setOriginalCopiedShopIds(route.stopsData.map((shop: any) => shop.id));
    }

    // Auto-set home address as start AND end if available
    if (user?.homeLat && user?.homeLng && user?.homeAddress) {
      const homeSelection = {
        address: user.homeAddress,
        lat: user.homeLat,
        lng: user.homeLng,
        source: 'home' as const
      };
      setContextStart(homeSelection);
      setContextEnd(homeSelection);
    }

    // Navigate to builder — autoOptimize=true triggers immediate recalculation
    navigate("/routes/new?personalize=true&autoOptimize=true");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="text-center py-16">
            <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4 animate-spin" />
            <h3 className="font-semibold text-lg mb-2">{t("routeDetail.loading")}</h3>
          </div>
        </main>
      </div>
    );
  }

  if (!route) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="text-center py-16">
            <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">{t("routeDetail.notFoundTitle")}</h3>
            <p className="text-muted-foreground mb-4">
              {t("routeDetail.notFoundDesc")}
            </p>
            <Link href="/routes">
              <Button>{t("routeBuilder.backToRoutes")}</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const stops = route.stopsData || [];
  const relatedPosts: any[] = [];

  // Calculate static map url to avoid loading Google Maps JS and Directions API needlessly
  // Preference order: 1. Pre-generated share image, 2. Live static map fallback
  const staticMapUrl_raw = route.share_image_url || (route.osm_route_data?.polyline ?
    `/api/osm/static-map/${route.country || 'NL'}?polyline=${encodeURIComponent(route.osm_route_data.polyline)}&markers=${stops.map((s: any) => `${s.lng},${s.lat}`).join('|')}`
    : getStaticMapUrl({
      size: "800x600",
      path: route.osm_route_data?.polyline || route.google_route_data?.polyline,
      markers: stops.map((s: any, i: number) => ({
        lat: s.lat,
        lng: s.lng,
        label: (i + 1).toString(),
        color: "0xF90F5A"
      }))
    }));

  const staticMapUrl = route.share_image_url
    ? `${route.share_image_url}${route.share_image_url.includes('?') ? '&' : '?'}v=${new Date(route.updated_at || Date.now()).getTime()}`
    : staticMapUrl_raw;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {itinerarySchema && <SchemaMarkup data={itinerarySchema} />}

      <main className="flex-1 container mx-auto px-4 py-8">
        <Link href="/routes">
          <Button variant="ghost" className="mb-6" data-testid="button-back">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("routeBuilder.backToRoutes")}
          </Button>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="lg:col-span-1 space-y-6 order-2 lg:order-1">
            <div className="hidden lg:block">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="min-w-0 flex-1">
                  <h1 className="hidden lg:block text-2xl md:text-3xl font-bold mb-2" data-testid="text-route-title">
                    {route.name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <User className="h-4 w-4" />
                      {route.creatorUsername}
                      {checkInsiderStatus(route.creatorUsername) && (
                        <InsiderStar size={14} className="ml-0.5" />
                      )}
                    </span>
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <Bookmark className="h-4 w-4" />
                      {t("routeDetail.onBucketlist", { count: (route.savesCount || 0) + (interactions.wishlist ? 1 : 0) })}
                    </span>
                  </div>

                  <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`h-4 w-4 ${star <= Math.round(route.vintmap_rating || 0) ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground fill-muted-foreground/20"}`}
                        />
                      ))}
                    </div>
                    <span>
                      {route.vintmap_rating ? Number(route.vintmap_rating).toFixed(1) : "0.0"} ({route.vintmap_review_count || 0})
                    </span>
                  </div>

                </div>
                <div className="flex flex-col items-end gap-2">
                  <Badge variant="secondary" className="text-base px-3 py-1 flex-shrink-0">
                    {t("routeDetail.stopsCount", { count: (route.stops || []).length })}
                  </Badge>
                  {(route.distance || route.osm_route_data?.distance || route.google_route_data?.distance) && (
                    <Badge variant="outline" className="text-primary border-primary/20 bg-primary/5 whitespace-nowrap">
                      {route.distance || route.osm_route_data?.distance || route.google_route_data?.distance}
                    </Badge>
                  )}
                </div>
              </div>

              {route.description && (
                <TranslatableText
                  table="routes"
                  rowId={route.id}
                  text={route.description}
                  translations={route.translations}
                  className="text-muted-foreground"
                />
              )}

              {/* km + duration directly under description */}
              {(route.distance || route.osm_route_data?.distance || route.google_route_data?.distance ||
                route.duration || route.osm_route_data?.duration || route.google_route_data?.duration) && (
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
                    {(route.distance || route.osm_route_data?.distance || route.google_route_data?.distance) && (
                      <span className="flex items-center gap-1 font-medium text-foreground">
                        <Navigation className="h-4 w-4 text-primary" />
                        {route.distance || route.osm_route_data?.distance || route.google_route_data?.distance}
                      </span>
                    )}
                    {(route.duration || route.osm_route_data?.duration || route.google_route_data?.duration) && (
                      <span className="flex items-center gap-1 font-medium text-foreground">
                        <Clock className="h-4 w-4 text-primary" />
                        {route.duration || route.osm_route_data?.duration || route.google_route_data?.duration}
                      </span>
                    )}
                  </div>
                )}

              <div className="flex flex-wrap gap-2 mt-4">
                <Button
                  onClick={() => handleToggleAction('favorite')}
                  variant={interactions.favorite ? "default" : "outline"}
                  className={interactions.favorite ? "bg-primary hover:bg-primary/90 border-primary" : ""}
                  data-testid="button-like-route"
                >
                  <Heart className={`h-4 w-4 mr-2 ${interactions.favorite ? 'fill-current' : ''}`} />
                  {t("routes.favorites.title")}
                </Button>
                <Button
                  onClick={() => handleToggleAction('wishlist')}
                  variant={interactions.wishlist ? "default" : "outline"}
                  data-testid="button-save-route"
                >
                  <Bookmark className={`h-4 w-4 mr-2 ${interactions.wishlist ? 'fill-current' : ''}`} />
                  {t("routes.wishlist.title")}
                </Button>
                <div className="flex flex-col gap-1 items-center">
                  <a
                    href={generateMapsUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      if (!isLoggedIn) {
                        e.preventDefault();
                        setShowLoginModal(true);
                        return;
                      }
                      if (route && stops.length > 0) {
                        logRouteNavAnalytics(route.id, stops.map((s: any) => s.id));
                      }
                    }}
                  >
                    <div className="flex flex-col gap-1 items-center">
                      {isInsider ? (
                        <Button data-testid="button-open-maps" className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white font-bold h-10 shadow-md">
                          ⚡ {t("nav.insiderStart.button") || "Start Direct Navigatie"}
                        </Button>
                      ) : (
                        <Button data-testid="button-open-maps" className="h-10">
                          🗺️ {t("nav.freeStart.button") || "Open in Google Maps"}
                        </Button>
                      )}
                      <span className="text-[10px] text-muted-foreground">
                        {isInsider
                          ? t("nav.insiderStart.desc") || "Route begint automatisch vanaf jouw locatie"
                          : t("nav.freeStart.desc") || "Pas startlocatie handmatig aan voor navigatie"}
                      </span>
                    </div>
                  </a>
                  {!isInsider && (
                    <Button variant="link" className="text-[10px] h-auto p-0 text-primary font-semibold" onClick={() => setShowUpgradeModal(true)}>
                      ⚡ {t("nav.freeStart.upsell") || "Word Insider en navigeer direct vanaf jouw locatie"}
                    </Button>
                  )}
                </div>
                <Button variant="ghost" data-testid="button-share" onClick={() => setShareModalOpen(true)}>
                  <Share2 className="h-4 w-4 mr-2" />
                  {t("routeDetail.share")}
                </Button>
                <div className="w-full h-0 md:hidden" /> {/* Spacer mobile */}
                <Button
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary/5 font-bold"
                  onClick={handlePersonalize}
                >
                  <Zap className="h-4 w-4 mr-2" />
                  {t("routeDetail.copyAsOwn")}
                </Button>
                {isLoggedIn && user?.id === route.user_id && (
                  <Link href={`/routes/edit/${route.id}`}>
                    <Button variant="outline">
                      <Edit2 className="h-4 w-4 mr-2" />
                      {t("routeDetail.editRoute")}
                    </Button>
                  </Link>
                )}
              </div>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-4">{t("routeDetail.stopsHeading", { count: stops.length })}</h2>
              <div className="space-y-4">
                {stops.map((shop: any, index: number) => (
                  <div key={shop!.id} className="pb-4">
                    <ShopCard shop={shop!} showAddButton={false} routeIndex={index + 1} />
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-8 border-t">
              <RouteReviews routeId={route.id} routeName={route.name} />
            </div>
          </div>

          {/* Right Column: Map & Summary (Top on mobile, Sticky on desktop) */}
          <div className="lg:col-span-1 space-y-6 order-1 lg:order-2 lg:sticky lg:top-8 self-start">
            {/* Mobile Header (Only visible on small screens) */}
            <div className="lg:hidden space-y-4">
              <h1 className="text-2xl font-bold">{route.name}</h1>
              {/* Badges moved to details block below map */}
            </div>

            {/* Route Map - Increased prominence */}
            <Card className="overflow-hidden border-none shadow-lg lg:border lg:shadow-sm">
              <CardHeader className="p-4 lg:p-6 pb-2 lg:pb-2">
                <CardTitle className="text-lg flex items-center justify-between">
                  {t("routeBuilder.mapTitle")}
                  <Badge variant="outline" className="text-[10px] h-5 lg:hidden">{t("routeDetail.stopsCount", { count: (route.stops || []).length })}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 lg:p-6">
                <div className="w-full aspect-square relative">
                  {staticMapUrl ? (
                    <img
                      src={staticMapUrl}
                      alt="Route overzichtskaart"
                      className="w-full h-full object-cover lg:rounded-lg"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (target.src.includes("/api/osm/static-map")) {
                          target.src = getStaticMapUrl({
                            path: route.osm_route_data?.polyline || route.google_route_data?.polyline,
                            markers: stops.map((s: any, i: number) => ({
                              lat: s.lat,
                              lng: s.lng,
                              label: (i + 1).toString()
                            })),
                            size: "800x800"
                          });
                        } else {
                          target.style.display = 'none';
                        }
                      }}
                    />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center lg:rounded-lg">
                      <MapPin className="h-10 w-10 text-muted-foreground" />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Rich Details for Mobile (User, Badges, Desc, Buttons) - Matches Desktop Left Col */}
            <div className="lg:hidden space-y-6">
              <div>
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="min-w-0 flex-1 pt-1">
                    {/* H1 hidden here, shown at top */}
                    <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1 whitespace-nowrap">
                        <User className="h-4 w-4" />
                        {route.creatorUsername}
                        {checkInsiderStatus(route.creatorUsername) && (
                          <InsiderStar size={14} className="ml-0.5" />
                        )}
                      </span>
                      <span className="flex items-center gap-1 whitespace-nowrap">
                        <Bookmark className="h-4 w-4" />
                        {t("routeDetail.onBucketlist", { count: (route.savesCount || 0) + (interactions.wishlist ? 1 : 0) })}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`h-3 w-3 ${star <= Math.round(route.vintmap_rating || 0) ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground fill-muted-foreground/20"}`}
                          />
                        ))}
                      </div>
                      <span>
                        {route.vintmap_rating ? Number(route.vintmap_rating).toFixed(1) : "0.0"} ({route.vintmap_review_count || 0})
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge variant="secondary" className="text-base px-3 py-1 flex-shrink-0 bg-black text-white hover:bg-black/90">
                      {t("routeDetail.stopsCount", { count: (route.stops || []).length })}
                    </Badge>
                    {(route.distance || route.osm_route_data?.distance || route.google_route_data?.distance) && (
                      <Badge variant="outline" className="text-primary border-primary/20 bg-primary/5 whitespace-nowrap">
                        {route.distance || route.osm_route_data?.distance || route.google_route_data?.distance}
                      </Badge>
                    )}
                  </div>
                </div>

                {route.description && (
                  <TranslatableText
                    table="routes"
                    rowId={route.id}
                    text={route.description}
                    translations={route.translations}
                    className="text-muted-foreground"
                  />
                )}

                {/* km + duration directly under description - mobile */}
                {(route.distance || route.osm_route_data?.distance || route.google_route_data?.distance ||
                  route.duration || route.osm_route_data?.duration || route.google_route_data?.duration) && (
                    <div className="flex items-center gap-4 text-sm mt-2">
                      {(route.distance || route.osm_route_data?.distance || route.google_route_data?.distance) && (
                        <span className="flex items-center gap-1 font-medium text-foreground">
                          <Navigation className="h-4 w-4 text-primary" />
                          {route.distance || route.osm_route_data?.distance || route.google_route_data?.distance}
                        </span>
                      )}
                      {(route.duration || route.osm_route_data?.duration || route.google_route_data?.duration) && (
                        <span className="flex items-center gap-1 font-medium text-foreground">
                          <Clock className="h-4 w-4 text-primary" />
                          {route.duration || route.osm_route_data?.duration || route.google_route_data?.duration}
                        </span>
                      )}
                    </div>
                  )}

                <div className="flex flex-wrap gap-2 mt-4">
                  <Button
                    onClick={() => handleToggleAction('favorite')}
                    variant={interactions.favorite ? "default" : "outline"}
                    className={`flex-1 ${interactions.favorite ? "bg-primary hover:bg-primary/90 border-primary" : "border-none bg-primary text-primary-foreground hover:bg-primary/90"}`}
                    data-testid="button-like-route-mobile"
                  >
                    <Heart className={`h-4 w-4 mr-2 ${interactions.favorite ? 'fill-current' : ''}`} />
                    {t("routes.favorites.title")}
                  </Button>
                  <Button
                    onClick={() => handleToggleAction('wishlist')}
                    variant={interactions.wishlist ? "default" : "outline"}
                    className={`flex-1 ${interactions.wishlist ? "bg-primary hover:bg-primary/90 border-primary" : "border-none bg-primary text-primary-foreground hover:bg-primary/90"}`}
                    data-testid="button-save-route-mobile"
                  >
                    <Bookmark className={`h-4 w-4 mr-2 ${interactions.wishlist ? 'fill-current' : ''}`} />
                    {t("routes.wishlist.title")}
                  </Button>
                </div>

                <div className="mt-2 space-y-2">
                  <div className="flex flex-col gap-1">
                    <a
                      href={generateMapsUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        if (!isLoggedIn) {
                          e.preventDefault();
                          setShowLoginModal(true);
                          return;
                        }
                        if (route && stops.length > 0) {
                          logRouteNavAnalytics(route.id, stops.map((s: any) => s.id));
                        }
                      }}
                      className="block w-full"
                    >
                      <div className="flex flex-col gap-1">
                        {isInsider ? (
                          <Button className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white font-bold h-12 shadow-md" data-testid="button-open-maps-mobile">
                            ⚡ {t("nav.insiderStart.button") || "Start Direct Navigatie"}
                          </Button>
                        ) : (
                          <Button className="w-full h-12" data-testid="button-open-maps-mobile">
                            🗺️ {t("nav.freeStart.button") || "Open in Google Maps"}
                          </Button>
                        )}
                        <span className="text-xs text-center text-muted-foreground mt-1 mb-1">
                          {isInsider
                            ? t("nav.insiderStart.desc") || "Route begint automatisch vanaf jouw locatie"
                            : t("nav.freeStart.desc") || "Pas startlocatie handmatig aan voor navigatie"}
                        </span>
                      </div>
                    </a>
                    {!isInsider && (
                      <Button variant="link" className="text-xs h-auto p-0 text-primary font-semibold mb-2" onClick={() => setShowUpgradeModal(true)}>
                        ⚡ {t("nav.freeStart.upsell") || "Word Insider en navigeer direct vanaf jouw locatie"}
                      </Button>
                    )}
                  </div>

                  <Button variant="ghost" className="w-full justify-start pl-0 hover:bg-transparent" data-testid="button-share-mobile" onClick={() => setShareModalOpen(true)}>
                    <Share2 className="h-4 w-4 mr-2" />
                    {t("routeDetail.share")}
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full border-primary/20 text-primary hover:bg-primary/5 font-bold"
                    onClick={handlePersonalize}
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    {t("routeDetail.copyAsOwn")}
                  </Button>
                  {isLoggedIn && user?.id === route.user_id && (
                    <Link href={`/routes/edit/${route.id}`} className="block w-full">
                      <Button variant="outline" className="w-full">
                        <Edit2 className="h-4 w-4 mr-2" />
                        {t("routeDetail.editRoute")}
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t("routeDetail.summary")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t("routeDetail.numberOfStops")}</span>
                  <span className="font-medium">{(route.stops || []).length}</span>
                </div>
                {(route.distance || route.osm_route_data?.distance || route.google_route_data?.distance) && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t("routeDetail.totalDistance")}</span>
                    <span className="font-medium">{route.distance || route.osm_route_data?.distance || route.google_route_data?.distance}</span>
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-muted-foreground">{t("routeDetail.provinces")}</span>
                  <span className="font-medium">
                    {route.allProvinces?.length > 0 ? route.allProvinces.join(", ") : (route.region || t("routeDetail.unknown"))}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-muted-foreground">{t("routeDetail.cities")}</span>
                  <span className="font-medium">
                    {route.allCities?.length > 0 ? route.allCities.join(", ") : (route.city || t("routeDetail.unknown"))}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t("routeDetail.saved")}</span>
                  <span className="font-medium">{(route.savesCount || 0) + (interactions.wishlist ? 1 : 0)}x</span>
                </div>

                <div className="pt-2 border-t mt-2">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("routeDetail.shopStatus")}</p>
                  </div>

                  <Accordion type="single" collapsible className="w-full">
                    {stops.map((s: any, i: number) => {
                      const todayIndex = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;
                      const todayStatus = s.openingHoursStructured ? getOpeningStatus(
                        s.openingHoursStructured,
                        todayIndex,
                        {
                          isClosed: !!s.is_temporarily_closed,
                          until: s.temporarily_closed_until,
                          reason: s.temporarily_closed_reason
                        },
                        { locale, t }
                      ) : null;
                      const dayNames = [t("routeDetail.dayShort.mon"), t("routeDetail.dayShort.tue"), t("routeDetail.dayShort.wed"), t("routeDetail.dayShort.thu"), t("routeDetail.dayShort.fri"), t("routeDetail.dayShort.sat"), t("routeDetail.dayShort.sun")];

                      return (
                        <AccordionItem value={`item-${i}`} key={i} className="border-b last:border-0 border-border/50">
                          <AccordionTrigger className="hover:no-underline py-3 hover:bg-muted/50 rounded-md px-1 transition-colors">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 sm:gap-3 w-full pr-2 items-start">
                              <span className="text-sm font-medium text-left">{s.name}</span>
                              <span className={`${todayStatus?.colorClass || "text-muted-foreground"} text-[10px] uppercase font-bold bg-muted/50 px-2 py-1 rounded flex-shrink-0`}>
                                {todayStatus?.status || t("routeDetail.unknown")}
                              </span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="pb-4">
                            <div className="grid grid-cols-7 gap-1 pt-1">
                              {dayNames.map((name, dayIndex) => {
                                const dayHours = s.openingHoursStructured?.find((h: any) => h.weekday === dayIndex);
                                const isToday = todayIndex === dayIndex;
                                const isClosed = !dayHours || dayHours.is_closed || !dayHours.open_time;

                                return (
                                  <div
                                    key={dayIndex}
                                    className={`flex flex-col items-center p-1 rounded border ${isToday
                                      ? 'bg-primary/5 border-primary/30 ring-1 ring-primary/20'
                                      : 'bg-muted/30 border-transparent'
                                      }`}
                                  >
                                    <span className={`text-[8px] uppercase font-bold ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>
                                      {name}
                                    </span>
                                    <span className={`text-[9px] mt-0.5 font-medium ${isClosed ? 'text-red-500' : 'text-foreground'}`}>
                                      {isClosed ? t("routeDetail.closedShort") : `${dayHours.open_time.slice(0, 2)}-${dayHours.close_time.slice(0, 2)}`}
                                    </span>
                                    <div className={`w-1 h-1 rounded-full mt-0.5 ${isClosed ? 'bg-red-500/50' : 'bg-green-500'}`} />
                                  </div>
                                );
                              })}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                </div>

                {isLoggedIn && (
                  <div className="pt-4 border-t">
                    <Button
                      variant="outline"
                      className="w-full border-primary text-primary hover:bg-primary/5 font-bold"
                      onClick={handlePersonalize}
                    >
                      <Zap className="h-4 w-4 mr-2" />
                      {t("routeDetail.personalize")}
                    </Button>
                    <p className="text-[10px] text-center mt-2 text-muted-foreground flex items-center justify-center gap-1">
                      <InsiderStar size={10} /> {t("routeDetail.insiderOnly")}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
            {relatedPosts.length > 0 && (
              <div>
                <h3 className="font-semibold mb-4">{t("routeDetail.routeFinds")}</h3>
                <div className="grid grid-cols-2 gap-3">
                  {relatedPosts.map(post => (
                    <PostCard key={post.id} post={post} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <LoginModal
        open={showLoginModal}
        onOpenChange={setShowLoginModal}
        action="save"
      />
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        variant="home"
      />
      <ShareModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        title={`${t("routeDetail.shareTitlePrefix")}: ${route?.name || ""}`}
        text={`${t("routeDetail.shareTextIntro")}\n\n🚗 ${route?.distance || t("routeDetail.distanceUnknown")} • ${t("routeDetail.stopsCount", { count: route?.stopsData?.length || 0 })}\n📍 ${route?.allCities?.join(", ") || route?.city || ""}\n\n${route?.description || ""}`}
        url={window.location.href}
        imageUrl={route?.share_image_url ? `${route.share_image_url}${route.share_image_url.includes('?') ? '&' : '?'}v=${new Date(route.updated_at || Date.now()).getTime()}` : (route?.osm_route_data?.polyline ?
          `/api/osm/static-map/${route?.country || 'NL'}?polyline=${encodeURIComponent(route.osm_route_data.polyline)}&markers=${route?.stopsData?.map((s: any) => `${s.lng},${s.lat}`).join('|')}`
          : getStaticMapUrl({
            size: "600x600",
            path: route?.osm_route_data?.polyline || route?.google_route_data?.polyline,
            markers: route?.stopsData?.map((s: any, i: number) => ({
              lat: s.lat,
              lng: s.lng,
              label: (i + 1).toString(),
              color: "0xF90F5A"
            }))
          }))}
        entityType="route"
        entityId={route?.id}
      />
    </div>
  );
}
